import { NETWORK_SUMMARY, NETWORK_FOUNDING, NETWORK_SEPARATE, NETWORK_TAGLINE } from "@/lib/network";
import { createFileRoute, Link } from "@tanstack/react-router";
import carlaiFounderAsset from "@/assets/carlai-founder.jpg.asset.json";
import { WaitlistForm } from "@/components/kit-forms";
import { KIT_TAGS } from "@/lib/kit.functions";
import { CEIC } from "@/lib/ceic";
import { CeicAnnouncement } from "@/components/ceic-announcement";
import { Button } from "@/components/ui/button";
import { Section, SectionHeading } from "@/components/layout";

/* ---------------------------------------------------------------------------
   Tables and Bridges - Homepage
   Movement-first structure: mission, beliefs, the work, free resource,
   agencies, founder, keynote, proof, invitation. Live form submissions are
   gated by the KIT_LIVE env var in src/lib/kit.functions.ts.
--------------------------------------------------------------------------- */

const R = {
  about: "/about",
  tos: "/trust-operating-system",
  ceoPractitionerNetwork: "https://ceopractitionernetwork.com",
  podcast: "/podcast",
  agencies: "/for-agencies",
  bookCarlai: "/book-carlai",
} as const;

const PAGE_TITLE = "Tables and Bridges | Community Engagement for Law Enforcement";
const PAGE_DESC =
  "Tables and Bridges equips law enforcement Community Engagement professionals and agencies with the methodology, community, and public conversation to build lasting trust.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tablesandbridges.com/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/" }],
  }),
  component: Home,
});

/* ============================== Page ============================== */

function Home() {
  return (
    <>
      <Hero />
      <Beliefs />
      <OurWork />
      <FreeResource />
      <ConferenceAnnouncement />
      <PractitionerStudyCallout />
      <AgencyInvitation />
      <OrganizationInvitation />
      <FounderSection />
      <Keynote />
      <Proof />
      <FinalCta />
      <CeicAnnouncement />
    </>
  );
}

/* =============================== 1. Hero =============================== */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-navy text-warm-white">
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.14]">
        <svg
          className="absolute inset-0 h-full w-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 1440 900"
          fill="none"
        >
          <defs>
            <pattern id="tb-grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M80 0H0V80" stroke="#F7F5EF" strokeWidth="0.5" strokeOpacity="0.35" />
            </pattern>
          </defs>
          <rect width="1440" height="900" fill="url(#tb-grid)" />
          <path d="M-40 720 L520 480 L920 560 L1500 320" stroke="#F2B84B" strokeWidth="1.25" />
          <circle cx="520" cy="480" r="4" fill="#F2B84B" />
          <circle cx="920" cy="560" r="4" fill="#F2B84B" />
          <path d="M0 820 H1440" stroke="#F7F5EF" strokeOpacity="0.25" />
          <path d="M0 120 H1440" stroke="#F7F5EF" strokeOpacity="0.15" />
        </svg>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(36,77,58,0.35),transparent_55%)]"
      />

      <div className="container-editorial relative z-10 pt-32 pb-28 md:pt-40 md:pb-36">
        <p className="eyebrow mb-8">
          A National Movement for Law Enforcement Community Engagement
        </p>

        <h1 className="max-w-5xl font-display text-warm-white text-[clamp(2.5rem,5.6vw,5.25rem)] leading-[1] tracking-[-0.02em]">
          Community Engagement is a professional discipline.
          <span className="block mt-4 italic text-gold">
            The work deserves a professional foundation.
          </span>
        </h1>

        <p className="mt-10 max-w-3xl text-warm-white/85 text-lg md:text-xl leading-relaxed">
          Tables and Bridges equips law enforcement Community Engagement
          professionals and agencies with the methodology, community, and public
          conversation required to build trust that lasts.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:items-center">
          <a href="#our-work" className="btn-base btn-primary">
            Explore the Work
          </a>
          <a
            href={R.ceoPractitionerNetwork}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-base btn-secondary-dark"
          >
            Explore the CEO Practitioner Network
          </a>
        </div>

        <p className="mt-8">
          <Link
            to="/for-organizations"
            className="text-sm text-warm-white/75 hover:text-gold underline-offset-4 hover:underline transition-colors"
          >
            Not in public safety? See how we work with organizations&nbsp;<span aria-hidden>→</span>
          </Link>
        </p>

        <div className="mt-20 flex items-center gap-6 text-xs uppercase tracking-[0.28em] text-warm-white/50">
          <span className="h-px w-12 bg-warm-white/40" aria-hidden />
          <span>Methodology · Community · Public Conversation</span>
        </div>
      </div>
    </section>
  );
}

/* =========================== 2. What We Believe =========================== */

const beliefs = [
  {
    n: "01",
    statement: "Trust is not an activity. It is an operating condition.",
    body: "Trust is either being strengthened or being spent. It shows up in how an agency makes decisions, keeps commitments, and answers for itself long before it shows up at an event.",
  },
  {
    n: "02",
    statement: "Community Engagement is not event planning. It is trust strategy.",
    body: "The work requires people who can read a trust condition, design engagement that matters, advise leadership honestly, and carry what is learned back into how the agency operates.",
  },
  {
    n: "03",
    statement: "The work has outgrown the way it has been described.",
    body: "Community Engagement professionals have been asked to carry strategic responsibility while also inventing the profession. This is the foundation that responsibility has been missing.",
  },
];

function Beliefs() {
  return (
    <section className="bg-warm-white text-navy section-y">
      <div className="container-editorial">
        <div className="max-w-3xl">
          <p className="eyebrow mb-6">What We Believe</p>
          <h2 className="font-display text-navy text-[clamp(2rem,3.8vw,3.25rem)] leading-[1.02]">
            Three convictions hold this{" "}
            <span className="text-gold italic">movement together.</span>
          </h2>
          <span aria-hidden className="divider-gold mt-8" />
        </div>

        <ul className="mt-16 grid gap-px border border-navy/10 bg-navy/10 md:grid-cols-3">
          {beliefs.map((b) => (
            <li key={b.n} className="flex flex-col bg-warm-white p-8 md:p-10">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-gold text-3xl md:text-4xl leading-none">
                  {b.n}
                </span>
                <span className="h-px flex-1 bg-navy/15" aria-hidden />
              </div>
              <p className="mt-6 font-display text-navy text-2xl leading-snug">
                {b.statement}
              </p>
              <p className="mt-5 text-[15px] text-navy/75 leading-relaxed">{b.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ============================= 3. Our Work ============================= */

type Pillar = {
  n: string;
  label: string;
  headline: string;
  body: string;
  cta: string;
  href: string;
  external?: boolean;
};

const pillars: Pillar[] = [
  {
    n: "01",
    label: "The Trust Operating System",
    headline: "The professional methodology for diagnosing and rebuilding trust.",
    body: "A connected system that helps practitioners and agencies locate where trust lives, diagnose what is weakening it, choose the right level of intervention, and prove what changed.",
    cta: "Explore the Methodology",
    href: R.tos,
  },
  {
    n: "02",
    label: "CEO Practitioner Network",
    headline: NETWORK_TAGLINE,
    body: `${NETWORK_SUMMARY} ${NETWORK_FOUNDING} ${NETWORK_SEPARATE}`,
    cta: "Explore the CEO Practitioner Network",
    href: R.ceoPractitionerNetwork,
    external: true,
  },
  {
    n: "03",
    label: "Tables and Bridges, the Podcast",
    headline: "Honest field conversations about the work.",
    body: "Practitioners, community partners, and leaders talking plainly about what building trust actually requires, what it costs, and what makes it hold.",
    cta: "Explore the Podcast",
    href: R.podcast,
  },
];

function OurWork() {
  return (
    <section id="our-work" className="bg-navy text-warm-white section-y">
      <div className="container-editorial">
        <div className="max-w-3xl">
          <p className="eyebrow mb-6">Our Work</p>
          <h2 className="font-display text-warm-white text-[clamp(2rem,3.8vw,3.5rem)] leading-[1.02]">
            Three pillars.{" "}
            <span className="text-gold italic">One foundation.</span>
          </h2>
          <p className="mt-6 text-warm-white/80 text-base md:text-lg leading-relaxed">
            Each part of Tables and Bridges serves a distinct purpose. Together
            they move Community Engagement from isolated effort to shared
            practice, and from visible activity to trust that can be carried,
            documented, and sustained.
          </p>
          <span aria-hidden className="divider-gold mt-8" />
        </div>

        <ul className="mt-16 grid gap-6 lg:grid-cols-3">
          {pillars.map((p) => (
            <li
              key={p.n}
              className="flex flex-col border border-warm-white/15 bg-navy p-8 md:p-10"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-display text-gold text-3xl leading-none">{p.n}</span>
                <span className="h-px flex-1 bg-warm-white/15" aria-hidden />
              </div>
              <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
                {p.label}
              </p>
              <h3 className="mt-4 font-display text-warm-white text-2xl leading-snug">
                {p.headline}
              </h3>
              <p className="mt-5 text-warm-white/75 text-[15px] leading-relaxed">
                {p.body}
              </p>
              <a
                href={p.href}
                target={p.external ? "_blank" : undefined}
                rel={p.external ? "noopener noreferrer" : undefined}
                className="mt-8 text-gold text-xs font-semibold uppercase tracking-[0.16em] hover:text-warm-white transition-colors"
              >
                {p.cta} →
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* =========================== 4. Free Resource =========================== */

function FreeResource() {
  return (
    <section id="free-guide" className="relative overflow-hidden bg-deep-green text-warm-white section-y scroll-mt-24">
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 1200 600"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <circle cx="1050" cy="120" r="220" stroke="#F2B84B" strokeWidth="0.75" />
          <circle cx="1050" cy="120" r="140" stroke="#F2B84B" strokeWidth="0.75" />
          <circle cx="1050" cy="120" r="70" stroke="#F2B84B" strokeWidth="0.75" />
        </svg>
      </div>

      <div className="container-editorial relative grid gap-12 lg:grid-cols-12 lg:gap-16 lg:items-center">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-6">Free Practitioner Resource</p>
          <h2 className="font-display text-warm-white text-[clamp(2rem,4.2vw,3.5rem)] leading-[1.02]">
            Why Engagement Fails{" "}
            <span className="text-gold italic">- And How to Fix It.</span>
          </h2>
          <span aria-hidden className="divider-gold mt-8" />
          <p className="mt-8 text-warm-white/85 text-lg leading-relaxed measure-body">
            A practitioner&apos;s guide to diagnosing trust conditions is coming
            soon. Enter your email to be the first to receive it.
          </p>
        </div>

        <div className="lg:col-span-5 border border-warm-white/25 bg-navy/40 p-8 md:p-10 self-start">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
            Coming Soon
          </p>
          <p className="mt-4 text-warm-white/85 text-[15px] leading-relaxed">
            We will send the guide the day it is released, along with the field
            notes that shaped it. No noise, and you can leave any time.
          </p>
          <div className="mt-7">
            <WaitlistForm
              tag={KIT_TAGS.guideWaitlist}
              ctaLabel="Notify Me"
              placeholder="Your work email"
              variant="onGreen"
              successMessage="You are on the list. The guide will land in your inbox first."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================== 4b. Conference ========================== */

function ConferenceAnnouncement() {
  return (
    <section className="bg-navy text-warm-white section-y">
      <div className="container-editorial">
        <div className="max-w-3xl">
          <p className="eyebrow mb-6">{CEIC.edition} · National Virtual Conference</p>
          <h2 className="font-display text-warm-white text-4xl md:text-5xl leading-tight tracking-normal">
            {CEIC.name}
          </h2>
          <p className="mt-6 text-lg font-semibold text-gold">{CEIC.dates} · {CEIC.format}</p>
          <span aria-hidden className="divider-gold mt-8 block" />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7 space-y-5 text-warm-white/85 text-base md:text-lg leading-relaxed">
            <p>
              <span className="font-display text-2xl italic text-gold">{CEIC.theme}.</span>{" "}
              Move beyond counting events toward designing engagement, documenting
              evidence, and building trust that an agency can sustain. Join a national
              gathering focused on Community Engagement as a professional discipline.
            </p>
            <p>
              March 30–31, 2027. Fully virtual and limited to 500 attendees.
              Registration is not yet open. The conference waitlist is for updates,
              not ticket purchases.
            </p>
            <p className="font-light text-warm-white/70 text-[15.5px] md:text-base">
              CEO Practitioner Network member admission is $129. Active membership
              is required and is separate from conference admission.
            </p>
          </div>

          <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-4 lg:justify-end">
            <Button asChild className="btn-base btn-primary h-auto whitespace-normal text-center"><Link to="/conference">Explore the Conference</Link></Button>
            <Button asChild variant="outline" className="btn-base btn-secondary-dark h-auto whitespace-normal text-center"><a href={CEIC.url} target="_blank" rel="noopener noreferrer">Official CEIC Site</a></Button>
            <a
              href={R.ceoPractitionerNetwork}
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-11 flex items-center justify-center text-center text-sm text-gold underline underline-offset-4"
            >
              Explore the CEO Practitioner Network
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================= 4c. National Practitioner Study ================= */

const STUDY_URL = "https://study.tablesandbridges.com/participate";

function PractitionerStudyCallout() {
  return (
    <section
      aria-labelledby="practitioner-study-heading"
      className="bg-deep-green text-warm-white section-y"
    >
      <div className="container-editorial">
        <div className="border-l-2 border-gold pl-6 md:pl-10 py-2">
          <p className="eyebrow mb-6">National Community Engagement Practitioner Study</p>
          <h2
            id="practitioner-study-heading"
            className="font-display text-warm-white text-[clamp(1.6rem,3vw,2.5rem)] leading-[1.08] max-w-3xl"
          >
            Your experience belongs in the{" "}
            <span className="text-gold italic">findings.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-warm-white/85 text-base md:text-lg leading-relaxed">
            Tables and Bridges is conducting a national study of how Community
            Engagement is practiced, supported, and measured across law
            enforcement agencies. If you do this work, the study needs your
            perspective. Responses are confidential, and results will help
            define the professional foundation the field has been missing.
          </p>
          <div className="mt-8">
            <a
              href={STUDY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-base btn-primary"
            >
              Take the Survey
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================== 5. For Agencies =========================== */

function AgencyInvitation() {
  return (
    <section className="bg-warm-white text-navy section-y">
      <div className="container-editorial grid gap-12 lg:grid-cols-12 lg:gap-20 lg:items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-6">For Agencies</p>
          <h2 className="font-display text-navy text-[clamp(2rem,4vw,3.5rem)] leading-[1.02]">
            Move from Community Engagement activity to{" "}
            <span className="text-gold italic">trust infrastructure.</span>
          </h2>
          <span aria-hidden className="divider-gold mt-8" />
        </div>
        <div className="lg:col-span-5 space-y-5 text-navy/80 text-base md:text-lg leading-relaxed measure-body">
          <p>
            Strong people cannot compensate for weak systems forever. Tables and
            Bridges works alongside agencies to develop Community Engagement
            personnel, clarify strategy, assess trust conditions, and build
            systems that carry relationships beyond one officer or one season.
          </p>
          <div className="pt-2">
            <a href={R.agencies} className="btn-base btn-navy">
              Work With Tables and Bridges
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =============================== 6. Founder =============================== */

function OrganizationInvitation() {
  return <Section tone="deep-green">
    <SectionHeading eyebrow="Beyond Public Safety" title="Trust inside the organization is where the work starts." lede="The same trust thinking that strengthens agencies helps any organization work better together. Tables and Bridges offers keynotes, training, facilitation, and consulting for teams across industries." />
    <Button asChild className="btn-base btn-primary mt-9 h-auto whitespace-normal"><Link to="/for-organizations">Work With Organizations</Link></Button>
  </Section>;
}

function FounderSection() {
  return (
    <section className="bg-navy text-warm-white section-y">
      <div className="container-editorial grid gap-14 lg:grid-cols-12 lg:gap-20 items-center">
        <figure className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-3 border border-gold/60 translate-x-3 translate-y-3"
            />
            <img
              src={carlaiFounderAsset.url}
              alt="Carlai Moore, founder of Tables and Bridges, in a professional field portrait."
              width={800}
              height={1000}
              loading="lazy"
              className="relative w-full aspect-[4/5] object-cover"
            />
          </div>
        </figure>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="eyebrow mb-6">Founded From the Field</p>
          <h2 className="font-display text-warm-white text-[clamp(2rem,4vw,3.5rem)] leading-[1.02]">
            Started by a practitioner, carried by a{" "}
            <span className="text-gold italic">community.</span>
          </h2>
          <span aria-hidden className="divider-gold mt-8" />
          <div className="mt-8 space-y-5 text-warm-white/85 text-base md:text-lg leading-relaxed measure-body">
            <p>
              Carlai Moore is a Police Sergeant and Community Engagement leader
              who started Tables and Bridges because the gap was real. Officers
              were being asked to build partnerships, represent their agencies,
              respond when trust was strained, advise leaders, and prove impact
              without a shared methodology or a professional home.
            </p>
            <p>
              Tables and Bridges was not built to sell a service. It was built
              to give the work a foundation, and it belongs to the practitioners,
              agency leaders, government partners, and community members who
              show up for it.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={R.about} className="btn-base btn-primary">
              About the Movement
            </a>
            <Link to="/book-carlai" className="btn-base btn-secondary-dark">
              Book Carlai
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================= 7. The Keynote ============================= */

function Keynote() {
  return (
    <section className="bg-warm-white text-navy section-y">
      <div className="container-editorial">
        <p className="eyebrow mb-6">Carlai Moore · Speaking</p>
        <h2 className="font-display !text-3xl sm:!text-4xl !tracking-normal max-w-3xl">Two signature keynotes. Purpose and trust in action.</h2>
        <p className="mt-7 max-w-3xl text-navy/80 text-lg leading-relaxed">Carlai Moore challenges people to reconnect with purpose, strengthen trust, and turn what they believe into action.</p>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div className="border-t border-navy/20 pt-6">
            <h3 className="!text-2xl !tracking-normal">Purpose Under Pressure</h3>
            <p className="mt-4 text-navy/80 leading-relaxed">For organizations, teams, and associations across industries. Find your PACE when change, uncertainty, and competing priorities make the mission hard.</p>
            <Button asChild variant="link" className="mt-4 p-0 h-auto min-h-11 text-deep-green"><Link to="/book-carlai" hash="purpose-under-pressure">Explore this keynote <span aria-hidden>→</span></Link></Button>
          </div>
          <div className="border-t border-navy/20 pt-6">
            <h3 className="!text-2xl !tracking-normal">The Invitation. The Table. The Bridge.</h3>
            <p className="mt-4 text-navy/80 leading-relaxed">For law enforcement and public safety audiences. Examine community engagement, meaningful listening, and the decisions and follow-through that strengthen trust.</p>
            <Button asChild variant="link" className="mt-4 p-0 h-auto min-h-11 text-deep-green"><Link to="/book-carlai" hash="invitation-table-bridge">Explore this keynote <span aria-hidden>→</span></Link></Button>
          </div>
        </div>
        <Button asChild className="btn-base btn-navy mt-9 h-auto"><Link to="/book-carlai">Explore Speaking</Link></Button>
      </div>
    </section>
  );
}

/* ================================ 8. Proof ================================ */

const testimonials = [
  {
    quote:
      "Carlai is a leader who makes those around her better. She values people and relationships, and I can say without a doubt she has contributed greatly to the success of Brookhaven Police Department, the City of Brookhaven, and the community.",
    name: "Abrem Ayana",
    role: "Supervisor, Brookhaven Police Department",
  },
  {
    quote:
      "Her respect for people coupled with patience allows her to move with ease to manage challenges, disrupt unfair situations, and mediate towards agreeable solutions. The outcomes on every community project we worked on together have been very successful.",
    name: "Dola Williams",
    role: "Colleague and community partner",
  },
  {
    quote:
      "Over the last year, I've been impressed by her amazing passion, dedication, and exceptional performance. She delivers an unmatched level of creativity, confidence, and quality. Be prepared to be blown away.",
    name: "Monty Bynum",
    role: "Colleague and leadership professional",
  },
];

function Proof() {
  const [feature, ...rest] = testimonials;
  return (
    <section className="bg-warm-white text-navy section-y border-t border-navy/10">
      <div className="container-editorial">
        <div className="max-w-3xl">
          <p className="eyebrow mb-6">From the Field</p>
          <h2 className="font-display text-navy text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.02]">
            Trusted for work that moves people and{" "}
            <span className="text-gold italic">organizations forward.</span>
          </h2>
          <span aria-hidden className="divider-gold mt-8" />
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-12 lg:gap-14">
          <figure className="lg:col-span-7 border-l-2 border-gold pl-6 md:pl-10">
            <blockquote className="font-display text-navy text-2xl md:text-[2rem] leading-snug">
              &ldquo;{feature.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-8 text-xs uppercase tracking-[0.22em]">
              <span className="text-gold font-semibold">{feature.name}</span>
              <span className="block mt-1 text-navy/75 tracking-[0.18em]">
                {feature.role}
              </span>
            </figcaption>
          </figure>

          <ul className="lg:col-span-5 grid gap-8">
            {rest.map((t) => (
              <li key={t.name} className="border-t border-navy/15 pt-6">
                <blockquote className="text-navy/85 text-[15.5px] leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-5 text-[11px] uppercase tracking-[0.22em]">
                  <span className="text-gold font-semibold">{t.name}</span>
                  <span className="block mt-1 text-navy/75">{t.role}</span>
                </figcaption>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ============================= 9. Final CTA ============================= */

function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-ink text-warm-white section-y">
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12]">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 1440 500"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <path d="M0 380 L440 240 L820 300 L1440 140" stroke="#F2B84B" strokeWidth="1" />
          <circle cx="440" cy="240" r="3.5" fill="#F2B84B" />
          <circle cx="820" cy="300" r="3.5" fill="#F2B84B" />
        </svg>
      </div>

      <div className="container-editorial relative">
        <p className="eyebrow mb-6">The Next Step</p>
        <h2 className="font-display text-warm-white text-[clamp(2.25rem,4.8vw,4rem)] leading-[1.02] max-w-4xl">
          The profession is ready for{" "}
          <span className="text-gold italic">more than activity.</span>
        </h2>
        <p className="mt-8 max-w-2xl text-warm-white/85 text-lg md:text-xl leading-relaxed">
          Join the practitioners, agency leaders, and community partners building
          a stronger standard for law enforcement Community Engagement.
        </p>
        <div className="mt-10 max-w-2xl border-l-2 border-gold pl-5">
          <h3 className="font-display text-warm-white text-2xl md:text-3xl">CEO Practitioner Network</h3>
          <p className="mt-2 text-gold italic text-lg">{NETWORK_TAGLINE}</p>
          <p className="mt-4 text-warm-white/85 leading-relaxed">{NETWORK_SUMMARY}</p>
          <p className="mt-3 text-warm-white font-semibold">{NETWORK_FOUNDING}</p>
          <p className="mt-2 text-warm-white/75 text-sm">{NETWORK_SEPARATE}</p>
        </div>
        <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:items-center">
          <a
            href={R.ceoPractitionerNetwork}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-base btn-primary"
          >
            Explore the CEO Practitioner Network
          </a>
          <Link
            to="/book-carlai"
            className="text-warm-white/90 hover:text-gold underline underline-offset-8 decoration-gold/60 hover:decoration-gold text-sm font-semibold tracking-[0.14em] uppercase transition-colors"
          >
            Book Carlai
          </Link>
        </div>
      </div>
    </section>
  );
}
